import { memo, useEffect, useRef, useState } from "react";
import { IonLabel, IonSkeletonText } from "@ionic/react";
import cx from "classnames";
import s from "./Giveaways.module.scss";
import useSuperGiveAwayStore from "../../../../CommonStores/giveaways/store";
import { Giveaway } from "../../../../../Common/models/giveaways";
import { isEmpty } from "lodash";
import { useBreakpoint } from "../../../../../Common/utils/common";

const GiveawayCard = ({
  giveaway,
  index,
}: {
  giveaway: Giveaway;
  index: number;
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageSrc = giveaway.imgUrl || giveaway.bannerImgUrl;

  useEffect(() => {
    const image = imageRef.current;
    if (image?.complete && image.naturalHeight > 0) {
      setImgLoaded(true);
    }
  }, [imageSrc]);

  const hasConsolationPrizes =
    !isEmpty(giveaway.firstRunnerUpPrize) ||
    !isEmpty(giveaway.secondRunnerUpPrize);

  return (
    <div key={giveaway.docId || index} className={s.card}>
      <div className={s.cardContent}>
        <div className={cx(s.imageContainer, { [s.loading]: !imgLoaded })}>
          <img
            ref={imageRef}
            src={imageSrc}
            alt={giveaway.title}
            className={s.giveawayImg}
            onLoad={() => setImgLoaded(true)}
            loading="lazy"
            decoding="async"
          />
          <IonSkeletonText
            className={s.backgroundImgLoader}
            style={{ opacity: imgLoaded ? 0 : 1 }}
            animated
          />
        </div>

        {/* Prize Information */}
        <div className={s.prizeInfo}>
          <IonLabel className={s.cardTitle}>{giveaway.title}</IonLabel>
          {hasConsolationPrizes && (
            <div className={s.prizeContainer}>
              {giveaway.firstRunnerUpPrize &&
                !isEmpty(giveaway.firstRunnerUpPrize) && (
                  <div className={s.prizeItem}>
                    <IonLabel className={s.prizeLabel}>
                      2<sup>ND</sup> PRIZE
                    </IonLabel>
                    <IonLabel className={s.prizeValue}>
                      {giveaway.firstRunnerUpPrize}
                    </IonLabel>
                  </div>
                )}

              {!isEmpty(giveaway.firstRunnerUpPrize) &&
                !isEmpty(giveaway.secondRunnerUpPrize) && (
                  <div className={s.prizeDivider}>|</div>
                )}

              {giveaway.secondRunnerUpPrize &&
                !isEmpty(giveaway.secondRunnerUpPrize) && (
                  <div className={s.prizeItem}>
                    <IonLabel className={s.prizeLabel}>
                      3<sup>RD</sup> PRIZE
                    </IonLabel>
                    <IonLabel className={s.prizeValue}>
                      {giveaway.secondRunnerUpPrize}
                    </IonLabel>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Giveaways = () => {
  const { ongoingGiveaway, upcomingGiveawayList } = useSuperGiveAwayStore();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";

  // Combine ongoing and upcoming giveaways, prioritizing ongoing
  const allGiveaways = [
    ...(ongoingGiveaway ? [ongoingGiveaway] : []),
    ...(upcomingGiveawayList || []),
  ];

  return (
    <div className={cx(s.giveawaysContainer, { [s.mobile]: isMobile })}>
      <IonLabel className={cx(s.title)}>YOU WILL MISS OUT ON THESE</IonLabel>
      <div className={s.cardsContainer}>
        {!isEmpty(allGiveaways)
          ? allGiveaways.map((giveaway, index) => (
              <GiveawayCard
                key={giveaway.docId || index}
                giveaway={giveaway}
                index={index}
              />
            ))
          : Array.from({ length: 2 }, (_, index) => (
              <div key={`empty-${index}`} className={s.card}>
                <div className={s.cardContent}>
                  <IonSkeletonText
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#2d2d2d",
                      borderRadius: "16px",
                    }}
                    animated
                  />
                </div>
              </div>
            ))}
      </div>
    </div>
  );
};

export default memo(Giveaways);
